export function pluralizeRu (count, one, few, many) {
  const n = Math.abs(Number(count) || 0) % 100
  const n1 = n % 10
  if (n > 10 && n < 20) return many
  if (n1 > 1 && n1 < 5) return few
  if (n1 === 1) return one
  return many
}

export function safeAmount (value) {
  const amount = Number(value)
  return Number.isFinite(amount) ? amount : 0
}

export function hasVisibleMoneyAmount (value) {
  return Math.round(Math.abs(safeAmount(value)) * 100) !== 0
}

export function percentOf (part, total) {
  const totalAmount = safeAmount(total)
  if (totalAmount <= 0) return 0
  return (safeAmount(part) / totalAmount) * 100
}

const QUARTER_FULL_LABELS = {
  1: 'I',
  2: 'II',
  3: 'III',
  4: 'IV',
}

export function getCurrentQuarter (date = new Date()) {
  return Math.floor(date.getMonth() / 3) + 1
}

export function getDepartmentIncomeHeroNote ({
  incomeQuarter,
  incomeYear,
  actualTotal = 0,
  now = new Date(),
}) {
  const selectedYear = Number(incomeYear)
  if (!Number.isFinite(selectedYear)) return null

  const currentYear = now.getFullYear()
  const currentQuarter = getCurrentQuarter(now)
  const isYear = incomeQuarter === 'year'

  if (isYear) {
    if (selectedYear < currentYear) return null
    if (selectedYear === currentYear) return 'Эту сумму получим, когда закроем год.'
    return safeAmount(actualTotal) <= 0
      ? `В ${selectedYear} году нет подписанных актов.`
      : null
  }

  const selectedQuarter = Number(incomeQuarter)
  if (!Number.isInteger(selectedQuarter) || selectedQuarter < 1 || selectedQuarter > 4) return null

  const selectedPeriodIndex = selectedYear * 4 + selectedQuarter
  const currentPeriodIndex = currentYear * 4 + currentQuarter

  if (selectedPeriodIndex < currentPeriodIndex) return null
  if (selectedPeriodIndex === currentPeriodIndex) {
    return 'Эту сумму получим, когда закроем квартал.'
  }

  return safeAmount(actualTotal) <= 0
    ? `В ${QUARTER_FULL_LABELS[selectedQuarter]} квартале ${selectedYear} нет подписанных актов.`
    : null
}

export function formatDateRuShort (value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function normalizeText (value) {
  return String(value || '').trim()
}

function getTeamId (team) {
  return team?.teamId || team?.team_id || team?.id || null
}

function getTeamDisplayName (team) {
  return normalizeText(team?.teamName || team?.team_name || team?.name) || 'Без команды'
}

function compactStageType (value) {
  const text = normalizeText(value)
  if (!text) return ''
  const [firstPart] = text.split('_')
  return normalizeText(firstPart) || text
}

function inferStageTypeFromName (stageName, contractNumber) {
  let name = normalizeText(stageName)
  const contract = normalizeText(contractNumber)

  if (contract) {
    for (const separator of [' — ', ' - ', ' – ']) {
      const prefix = `${contract}${separator}`
      if (name.startsWith(prefix)) {
        name = normalizeText(name.slice(prefix.length))
        break
      }
    }
  }

  return compactStageType(name)
}

function getStageTypeLabel (stage) {
  return compactStageType(
    stage?.stageType ||
    stage?.stage_type ||
    stage?.distributionType ||
    stage?.distribution_type ||
    stage?.type
  ) || inferStageTypeFromName(stage?.stageName || stage?.stage_name, stage?.contractNumber || stage?.contract_number)
}

function getContractStageLabel (stage) {
  const contractNumber = normalizeText(stage?.contractNumber || stage?.contract_number)
  const stageType = getStageTypeLabel(stage)
  if (contractNumber && stageType) return `${contractNumber} — ${stageType}`
  return stageType || normalizeText(stage?.stageName || stage?.stage_name || stage?.stageId || stage?.stage_id) || 'Без стадии'
}

function normalizeStageStatus (status) {
  const text = normalizeText(status)
  if (!text) return ''
  if (text.toLowerCase().includes('заакт')) return 'закрыта'
  return text
}

export function getDepartmentInitials (name) {
  const parts = String(name || '')
    .replace(/[—–-]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
  if (!parts.length) return 'ПД'

  const firstShortCode = parts.find((part) => /^[А-ЯA-Z]{2,4}\d?$/u.test(part))
  if (firstShortCode) return firstShortCode.slice(0, 3).toUpperCase()

  return parts
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
}

export function getDepartmentLeadName (department) {
  const head = department?.heads?.find((item) => item?.full_name)
  if (!head) return 'Руководитель не назначен'

  const name = normalizeText(head.full_name)
  const position = normalizeText(head.position || head.employee_position || head.role || head.employee_role)
  return position ? `${name}, ${position}` : name
}

export function getDepartmentSubtitle (department, teamsCountFallback = undefined) {
  const type = department?.department_type || 'Тип не указан'
  const employeesCount = Number(department?.employees_count ?? department?.employees?.length ?? 0)
  const visibleTeamsCount = Number(teamsCountFallback)
  const teamsCount = Number.isFinite(visibleTeamsCount)
    ? visibleTeamsCount
    : Number(department?.teams_count ?? 0)
  return [
    type,
    getDepartmentLeadName(department),
    `${employeesCount} ${pluralizeRu(employeesCount, 'сотрудник', 'сотрудника', 'сотрудников')}`,
    `${teamsCount} ${pluralizeRu(teamsCount, 'команда', 'команды', 'команд')}`,
  ].join(' · ')
}

export function pluralizeStages (count) {
  return pluralizeRu(count, 'стадия', 'стадии', 'стадий')
}

export function groupPlannedByTeam (stagesIncome) {
  if (!stagesIncome?.length) return []

  const byTeam = {}
  for (const stage of stagesIncome) {
    const stageId = stage.stage_id || 'no_stage'
    const stageName = getContractStageLabel(stage)
    for (const team of stage.teams || []) {
      const teamId = getTeamId(team)
      const teamName = getTeamDisplayName(team)
      const teamKey = teamId ? `id:${teamId}` : `name:${teamName}`
      const stageTotal = safeAmount(team.total_income ?? (team.employees || []).reduce(
        (sum, employee) => sum + (Number(employee.income) || 0),
        0,
      ))
      const previouslyActAmount = safeAmount(team.previously_act_amount ?? team.previouslyActAmount ?? (team.employees || []).reduce(
        (sum, employee) => sum + safeAmount(employee.previously_act_amount ?? employee.previouslyActAmount),
        0,
      ))
      const actsIncomeAmount = safeAmount(team.acts_income_amount ?? team.actsIncomeAmount ?? team.current_year_act_amount ?? (team.employees || []).reduce(
        (sum, employee) => sum + safeAmount(employee.acts_income_amount ?? employee.actsIncomeAmount ?? employee.current_year_act_amount),
        0,
      ))
      const totalActAmount = safeAmount(team.total_act_amount ?? team.totalActAmount ?? previouslyActAmount + actsIncomeAmount)
      const remainingIncome = safeAmount(team.remaining_income ?? team.remainingIncome ?? Math.max(stageTotal - totalActAmount, 0))
      if (!byTeam[teamKey]) {
        byTeam[teamKey] = { teamId, teamName, total: 0, stages: [] }
      }
      byTeam[teamKey].total += stageTotal
      byTeam[teamKey].stages.push({
        stageId,
        stageName,
        contractNumber: stage.contract_number || '',
        stageType: stage.stage_type || stage.distribution_type || stage.type || '',
        objectStageName: stage.object_stage_name || '',
        objectStageId: stage.object_stage_id || null,
        objectId: stage.object_id || null,
        objectName: stage.object_name || '',
        stageBudget: stage.stage_budget ?? stage.budget ?? null,
        plannedDate: stage.planned_date || null,
        factDate: stage.fact_date || null,
        completionDate: stage.completion_date || null,
        status: normalizeStageStatus(stage.status) || 'В работе',
        total: stageTotal,
        previouslyActAmount,
        actsIncomeAmount,
        totalActAmount,
        remainingIncome,
        employees: team.employees || [],
      })
    }
  }

  const teams = Object.values(byTeam).sort((a, b) => (a.teamName || '').localeCompare(b.teamName || ''))
  teams.forEach((team) => {
    team.stages.sort((a, b) => (a.stageName || '').localeCompare(b.stageName || ''))
  })
  return teams
}

export function buildTeamSummaries ({ teams = [], unassignedEmployees = [], plannedTeams = [], actualTeams = [] }) {
  const summariesByKey = new Map()

  teams.forEach((team) => {
    const members = team.members || []
    const teamId = getTeamId(team)
    summariesByKey.set(`team:${team.id}`, {
      key: `team:${team.id}`,
      id: teamId,
      name: getTeamDisplayName(team),
      members,
      membersCount: members.length,
      leadName: members[0]?.full_name || 'руководитель не назначен',
      plannedStagesCount: 0,
      actualStagesCount: 0,
      subTeams: team.sub_teams || [],
      usesSubTeams: Boolean(team.uses_sub_teams || (team.sub_teams || []).length),
      source: 'team',
    })
  })

  if (unassignedEmployees.length) {
    summariesByKey.set('unassigned', {
      key: 'unassigned',
      id: null,
      name: 'Без команды',
      members: unassignedEmployees,
      membersCount: unassignedEmployees.length,
      leadName: 'без руководителя',
      plannedStagesCount: 0,
      actualStagesCount: 0,
      source: 'unassigned',
    })
  }

  const findById = (teamId) => teamId
    ? [...summariesByKey.values()].find((team) => team.id === teamId)
    : null
  const findByName = (name) => [...summariesByKey.values()].find((team) => team.name === name)
  plannedTeams.forEach((team) => {
    const teamId = getTeamId(team)
    const teamName = getTeamDisplayName(team)
    const existing = findById(teamId) || findByName(teamName)
    if (existing) {
      existing.plannedStagesCount = team.stages?.length || 0
      return
    }
    const key = teamId ? `income:${teamId}` : `income:${teamName}`
    summariesByKey.set(key, {
      key,
      id: teamId,
      name: teamName,
      members: [],
      membersCount: 0,
      leadName: 'по доходам',
      plannedStagesCount: team.stages?.length || 0,
      actualStagesCount: 0,
      source: 'income',
    })
  })

  actualTeams.forEach((team, index) => {
    const teamId = getTeamId(team)
    const teamName = getTeamDisplayName(team)
    const existing = findById(teamId) || findByName(teamName)
    if (existing) {
      existing.actualStagesCount = team.stages?.length || 0
      return
    }
    const key = `actual:${teamId || teamName || index}`
    summariesByKey.set(key, {
      key,
      id: teamId,
      name: teamName,
      members: [],
      membersCount: 0,
      leadName: 'по актам',
      plannedStagesCount: 0,
      actualStagesCount: team.stages?.length || 0,
      source: 'income',
    })
  })

  return [...summariesByKey.values()].filter((team) => {
    if (team.source !== 'team') return true
    return team.membersCount > 0 || team.plannedStagesCount > 0 || team.actualStagesCount > 0
  })
}

export function findPlannedTeam (plannedTeams, activeTeam) {
  const activeTeamId = typeof activeTeam === 'string' ? null : getTeamId(activeTeam)
  const activeTeamName = typeof activeTeam === 'string' ? activeTeam : getTeamDisplayName(activeTeam)
  if (activeTeamId) {
    const byId = (plannedTeams || []).find((team) => getTeamId(team) === activeTeamId)
    if (byId) return byId
  }
  return (plannedTeams || []).find((team) => getTeamDisplayName(team) === activeTeamName) || null
}

export function findActualTeam (actualTeams, activeTeam) {
  const activeTeamId = typeof activeTeam === 'string' ? null : getTeamId(activeTeam)
  const activeTeamName = typeof activeTeam === 'string' ? activeTeam : getTeamDisplayName(activeTeam)
  if (activeTeamId) {
    const byId = (actualTeams || []).find((team) => getTeamId(team) === activeTeamId)
    if (byId) return byId
  }
  return (actualTeams || []).find((team) => getTeamDisplayName(team) === activeTeamName) || null
}

export function buildPlannedAnalytics (plannedTeam) {
  const employeesByName = new Map()
  const projectsByName = new Map()
  const matrixRows = []

  for (const stage of plannedTeam?.stages || []) {
    const stageEmployees = stage.employees || []
    for (const employee of stageEmployees) {
      const employeeName = employee.employee_name || '—'
      const amount = safeAmount(employee.income)
      const prev = employeesByName.get(employeeName) || {
        name: employeeName,
        role: employee.employee_role || employee.role || 'Сотрудник',
        total: 0,
        stagesCount: 0,
        employee,
      }
      prev.total += amount
      prev.stagesCount += 1
      employeesByName.set(employeeName, prev)
    }

    const projectName = stage.contractNumber || 'Без договора'
    const project = projectsByName.get(projectName) || {
      name: projectName,
      stageName: stage.stageName,
      total: 0,
      stagesCount: 0,
    }
    project.total += safeAmount(stage.total)
    project.stagesCount += 1
    project.stages = project.stages || []
    project.stages.push({
      ...stage,
      total: safeAmount(stage.total),
    })
    projectsByName.set(projectName, project)

    matrixRows.push({
      id: stage.stageId,
      stageName: stage.stageName,
      projectName,
      employees: stageEmployees.map((employee) => ({
        name: employee.employee_name || '—',
        amount: safeAmount(employee.income),
        percent: percentOf(employee.income, stage.total),
      })),
      total: safeAmount(stage.total),
    })
  }

  const total = safeAmount(plannedTeam?.total)
  const employees = [...employeesByName.values()].sort((a, b) => b.total - a.total)
  employees.forEach((employee) => {
    employee.percent = percentOf(employee.total, total)
  })

  return {
    total,
    employees,
    projects: [...projectsByName.values()].sort((a, b) => b.total - a.total),
    matrixRows,
    stages: plannedTeam?.stages || [],
  }
}

export function buildActualAnalytics (actualTeam) {
  const employeesByName = new Map()
  const projectsByName = new Map()
  const matrixRows = []

  for (const stage of actualTeam?.stages || []) {
    const stageName = getContractStageLabel(stage)
    const stageType = getStageTypeLabel(stage)
    const contractNumber = stage.contract_number || ''
    const objectName = stage.object_name || 'Без объекта'
    const objectId = stage.object_id || null
    const employees = stage.employees || []
    const stageTotal = safeAmount(stage.sum ?? stage.total)
    const acts = (stage.acts || []).map((act) => {
      const actTotal = safeAmount(act.sum ?? act.total)
      return {
        ...act,
        id: act.act_unique_number || `${stage.stage_id || stageName}-${act.act_date || ''}`,
        label: act.act_date ? `Акт от ${formatDateRuShort(act.act_date)}` : `Акт ${act.act_unique_number || ''}`.trim(),
        total: actTotal,
        employees: (act.employees || []).map((employee) => ({
          name: employee.employee_name || '—',
          amount: safeAmount(employee.sum ?? employee.income),
          percent: percentOf(employee.sum ?? employee.income, actTotal),
          subTeam: normalizeText(employee.sub_team),
          rolePercent: employee.employee_pct_in_team == null || employee.employee_pct_in_team === ''
            ? null
            : safeAmount(employee.employee_pct_in_team),
        })),
      }
    })

    employees.forEach((employee) => {
      const employeeName = employee.employee_name || '—'
      const amount = safeAmount(employee.sum ?? employee.income)
      const prev = employeesByName.get(employeeName) || {
        name: employeeName,
        role: employee.employee_role || employee.role || 'Сотрудник',
        total: 0,
        stagesCount: 0,
        employee,
      }
      prev.total += amount
      prev.stagesCount += 1
      employeesByName.set(employeeName, prev)
    })

    const projectKey = objectId ? `object:${objectId}` : `object-name:${objectName}`
    const project = projectsByName.get(projectKey) || {
      name: objectName,
      objectId,
      total: 0,
      stagesCount: 0,
      stages: [],
    }
    project.total += stageTotal
    project.stagesCount += 1
    project.stages.push({
      id: stage.stage_id || stageName,
      stageName,
      stageType,
      contractNumber,
      objectId,
      objectName,
      stageBudget: stage.stage_budget ?? stage.budget ?? null,
      total: stageTotal,
    })
    projectsByName.set(projectKey, project)

    matrixRows.push({
      id: stage.stage_id || stageName,
      stageName,
      stageType,
      projectName: objectName,
      contractNumber,
      objectId,
      objectName,
      employees: employees.map((employee) => ({
        name: employee.employee_name || '—',
        amount: safeAmount(employee.sum ?? employee.income),
        percent: percentOf(employee.sum ?? employee.income, stageTotal),
        subTeam: normalizeText(employee.sub_team),
        rolePercent: employee.employee_pct_in_team == null || employee.employee_pct_in_team === ''
          ? null
          : safeAmount(employee.employee_pct_in_team),
      })),
      total: stageTotal,
      acts,
    })
  }

  const total = safeAmount(actualTeam?.sum ?? actualTeam?.total)
  const employees = [...employeesByName.values()].sort((a, b) => b.total - a.total)
  employees.forEach((employee) => {
    employee.percent = percentOf(employee.total, total)
  })

  return {
    total,
    employees,
    projects: [...projectsByName.values()].sort((a, b) => b.total - a.total),
    matrixRows,
    stages: actualTeam?.stages || [],
  }
}

export function buildStageOverviewRows (plannedTeam, actualTeam) {
  const actualByStage = new Map()
  for (const stage of actualTeam?.stages || []) {
    const key = String(stage.stage_id || stage.stage_name || '').trim()
    if (!key) continue
    actualByStage.set(key, {
      total: safeAmount(stage.sum ?? stage.total),
      stage,
    })
  }

  const plannedStageIds = new Set()
  const plannedRows = []

  for (const stage of plannedTeam?.stages || []) {
    const key = String(stage.stageId || stage.stage_id || stage.stageName || stage.stage_name || '').trim()
    if (key) plannedStageIds.add(key)
    const actual = actualByStage.get(key)
    const actualStage = actual?.stage || {}
    const stageName = getContractStageLabel(stage)
    const planned = safeAmount(stage.total ?? stage.total_income)
    const fact = safeAmount(stage.totalActAmount ?? stage.total_act_amount ?? actual?.total)
    const remaining = (stage.remainingIncome ?? stage.remaining_income) == null
      ? Math.max(planned - fact, 0)
      : safeAmount(stage.remainingIncome ?? stage.remaining_income)
    if (!hasVisibleMoneyAmount(planned)) continue
    const objectId = stage.objectId || actualStage.object_id || null
    const objectName = stage.objectName || actualStage.object_name || ''
    plannedRows.push({
      id: key || stageName,
      stageName,
      stageType: getStageTypeLabel(stage),
      objectStageName: stage.objectStageName || stage.object_stage_name || '',
      objectStageId: stage.objectStageId || stage.object_stage_id || null,
      projectName: stage.contractNumber || stage.contract_number || 'Без договора',
      contractNumber: stage.contractNumber || stage.contract_number || actualStage.contract_number || '',
      objectId,
      objectName: objectName || 'Без объекта',
      budget: safeAmount(stage.stageBudget ?? stage.stage_budget ?? stage.budget),
      plan: planned,
      fact,
      remaining,
      completionPercent: planned > 0 ? Math.min(100, Math.round((fact / planned) * 100)) : 0,
      status: normalizeStageStatus(stage.status || stage.stage_status) || (fact > 0 ? 'В работе' : 'Новая'),
      dueDate: stage.plannedDate || stage.planned_date || stage.completionDate || stage.completion_date || stage.factDate || stage.fact_date || null,
    })
  }

  const existingIds = new Set(plannedRows.map((row) => row.id))
  for (const stage of actualTeam?.stages || []) {
    const key = String(stage.stage_id || stage.stage_name || '').trim()
    if (plannedStageIds.has(key) || existingIds.has(key)) continue
    const fact = safeAmount(stage.sum ?? stage.total)
    if (!hasVisibleMoneyAmount(fact)) continue
    plannedRows.push({
      id: key || stage.stage_name,
      stageName: getContractStageLabel(stage),
      stageType: getStageTypeLabel(stage),
      objectStageName: stage.object_stage_name || '',
      objectStageId: stage.object_stage_id || null,
      projectName: stage.contract_number || 'Без договора',
      contractNumber: stage.contract_number || '',
      objectId: stage.object_id || null,
      objectName: stage.object_name || 'Без объекта',
      budget: 0,
      plan: 0,
      fact,
      remaining: 0,
      completionPercent: fact > 0 ? 100 : 0,
      status: normalizeStageStatus(stage.status || stage.stage_status) || 'В работе',
      dueDate: null,
    })
  }

  return plannedRows.sort((a, b) =>
    (a.objectName || '').localeCompare(b.objectName || '', 'ru') ||
    (a.projectName || '').localeCompare(b.projectName || '', 'ru') ||
    (a.stageName || '').localeCompare(b.stageName || '', 'ru')
  )
}

function getEmployeeSubTeam (employee) {
  return normalizeText(employee?.sub_team)
}

function filterEmployeesBySubTeam (employees = [], subTeamKey) {
  if (!subTeamKey || subTeamKey === 'all') return employees
  return employees.filter((employee) => getEmployeeSubTeam(employee) === subTeamKey)
}

function sumEmployeeAmounts (employees = []) {
  return employees.reduce((sum, employee) => sum + safeAmount(employee.sum ?? employee.income), 0)
}

function employeeHasStageOverviewAmounts (employee) {
  return [
    employee?.previously_act_amount,
    employee?.previouslyActAmount,
    employee?.acts_income_amount,
    employee?.actsIncomeAmount,
    employee?.current_year_act_amount,
    employee?.total_act_amount,
    employee?.totalActAmount,
    employee?.remaining_income,
    employee?.remainingIncome,
  ].some((value) => value != null)
}

function sumEmployeeStageOverviewAmounts (employees = []) {
  return employees.reduce((totals, employee) => {
    const previously = safeAmount(employee.previously_act_amount ?? employee.previouslyActAmount)
    const acts = safeAmount(employee.acts_income_amount ?? employee.actsIncomeAmount ?? employee.current_year_act_amount)
    const totalAct = safeAmount(employee.total_act_amount ?? employee.totalActAmount ?? previously + acts)
    const remaining = (employee.remaining_income ?? employee.remainingIncome) == null
      ? Math.max(safeAmount(employee.income ?? employee.sum) - totalAct, 0)
      : safeAmount(employee.remaining_income ?? employee.remainingIncome)
    return {
      previouslyActAmount: totals.previouslyActAmount + previously,
      actsIncomeAmount: totals.actsIncomeAmount + acts,
      totalActAmount: totals.totalActAmount + totalAct,
      remainingIncome: totals.remainingIncome + remaining,
    }
  }, {
    previouslyActAmount: 0,
    actsIncomeAmount: 0,
    totalActAmount: 0,
    remainingIncome: 0,
  })
}

export function resolveTeamSummaryFromUrlParam (teamSummaries = [], teamParam) {
  if (!teamParam || !teamSummaries.length) return null
  const normalized = decodeURIComponent(String(teamParam)).trim()
  if (!normalized) return null
  const lowered = normalized.toLowerCase()
  return teamSummaries.find((team) => {
    if (team.id && String(team.id) === normalized) return true
    if (team.key && String(team.key) === normalized) return true
    if (team.name && team.name === normalized) return true
    if (team.name && team.name.toLowerCase() === lowered) return true
    return false
  }) || null
}

export function resolveSubTeamTabKeyFromUrlParam (subTeamTabs = [], subTeamParam) {
  if (!subTeamParam || !subTeamTabs.length) return null
  const normalized = decodeURIComponent(String(subTeamParam)).trim()
  if (!normalized) return null
  const exact = subTeamTabs.find((tab) => tab.key === normalized)
  if (exact) return exact.key
  const lowered = normalized.toLowerCase()
  const insensitive = subTeamTabs.find((tab) => String(tab.key).toLowerCase() === lowered)
  return insensitive?.key ?? null
}

export function buildSubTeamTabs (activeTeam, departmentName = '') {
  if (!activeTeam?.usesSubTeams) return []
  const subTeams = [...(activeTeam.subTeams || [])].sort((a, b) => a.localeCompare(b, 'ru'))
  if (!subTeams.length) return []
  const wholeLabel = departmentName
    ? `Весь ${departmentName}`
    : `Вся ${activeTeam.name}`
  return [{ key: 'all', label: wholeLabel }, ...subTeams.map((tag) => ({ key: tag, label: tag }))]
}

export function filterIncomeTeamBySubTeam (teamData, subTeamKey) {
  if (!teamData || !subTeamKey || subTeamKey === 'all') return teamData

  const stages = (teamData.stages || []).map((stage) => {
    const employees = filterEmployeesBySubTeam(stage.employees, subTeamKey)
    const acts = (stage.acts || []).map((act) => {
      const actEmployees = filterEmployeesBySubTeam(act.employees, subTeamKey)
      return {
        ...act,
        employees: actEmployees,
        sum: sumEmployeeAmounts(actEmployees),
      }
    }).filter((act) => act.employees.length > 0 || safeAmount(act.sum) > 0)

    const stageSum = sumEmployeeAmounts(employees)
    const hasOverviewAmounts = employees.some(employeeHasStageOverviewAmounts)
    const overviewAmounts = hasOverviewAmounts
      ? sumEmployeeStageOverviewAmounts(employees)
      : null
    return {
      ...stage,
      employees,
      acts,
      sum: stageSum,
      total: stageSum,
      ...(overviewAmounts ? {
        previouslyActAmount: overviewAmounts.previouslyActAmount,
        previously_act_amount: overviewAmounts.previouslyActAmount,
        actsIncomeAmount: overviewAmounts.actsIncomeAmount,
        acts_income_amount: overviewAmounts.actsIncomeAmount,
        totalActAmount: overviewAmounts.totalActAmount,
        total_act_amount: overviewAmounts.totalActAmount,
        remainingIncome: overviewAmounts.remainingIncome,
        remaining_income: overviewAmounts.remainingIncome,
      } : {}),
    }
  }).filter((stage) => stage.employees.length > 0 || safeAmount(stage.sum ?? stage.total) > 0)

  const teamSum = stages.reduce((sum, stage) => sum + safeAmount(stage.sum ?? stage.total), 0)
  return {
    ...teamData,
    stages,
    sum: teamSum,
    total: teamSum,
  }
}
